import { useEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';
import { 
  GridColDef, 
  GridRowSelectionModel, 
  GridRenderCellParams 
} from '@mui/x-data-grid';
import DataTableGrid from '@bumble/app/components/DataTableGrid';
import { Indicator } from '@bumble/app/components/Indicator';
import NoData from '@bumble/app/components/NoData';
import { Pdu, PduOutletState } from '@bumble/app/__generated__/data-contracts';
import EditIcon from '@mui/icons-material/Edit';
import DialogStandard from '@bumble/app/components/DialogStandard';
import { PduOutletEdit } from './PduOutletEdit';
import { toast } from 'react-toastify';
import { useMutation } from '@tanstack/react-query';
import { useApi } from '@bumble/app/hooks/use-api';
import { apiError } from '@bumble/app/utils/api-error';
import { IOSSwitch } from '@bumble/app/components/IOSSwitch';
import { PageLoading } from '@bumble/app/components/PageLoading';

interface PduOutletsProps {
  data: Pdu | undefined;
  onOutletUpdate?: () => void;
  isRefetching?: boolean;
}

export const PduOutlets = ({ data, onOutletUpdate, isRefetching = false }: PduOutletsProps) => {
  const api = useApi();
  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>([]);
  const [showEditDialog, setShowEditDialog] = useState<boolean>(false);
  const [menuItems, setMenuItems] = useState<{
    label: string;
    icon: React.ReactNode;
    onClick: () => void;
    hasDivider?: boolean;
    disabled?: boolean;
  }[]>([]);
  const [togglingOutlets, setTogglingOutlets] = useState<Set<number>>(new Set());
  const [isRefreshingAfterToggle, setIsRefreshingAfterToggle] = useState(false);
  const [togglingContent, setTogglingContent] = useState<string>('');

  if (data === undefined) return <NoData />;

  const outlets = data.outlets || [];
  const pduId = data.pdu_id || '';

  const toggleOutletMutation = useMutation(api.togglePduOutletStateApiV1TogglePduOutletStatePost, {
    onSuccess: () => {
      toast.success('Outlet state toggled successfully.');
      if (onOutletUpdate) {
        setTimeout(() => {
          onOutletUpdate();
        }, 2000);
      } else {
        setIsRefreshingAfterToggle(false);
        setTogglingContent('');
      }
    },
    onError: (error: any) => {
      apiError(error);
      setIsRefreshingAfterToggle(false);
      setTogglingContent('');
    },
    onSettled: (_, __, variables) => {
      if (variables?.outlet_id != null) {
        setTogglingOutlets((prev) => {
          const next = new Set(prev);
          next.delete(variables.outlet_id);
          return next;
        });
      }
    },
  });

  const handleToggleSplit = (outletId: string | number, currentState: string) => {
    const id = Number(outletId);
    if (!pduId || (outletId !== 0 && outletId !== '0' && !outletId) || isNaN(id)) {
      toast.error('Missing required parameters.');
      return;
    }
    const newState = currentState === 'on' ? PduOutletState.Off : PduOutletState.On;
    setTogglingOutlets((prev) => new Set(prev).add(id));
    setTogglingContent(currentState === 'on' ? 'Turning Action Off' : 'Turning Action On');
    setIsRefreshingAfterToggle(true);
    toggleOutletMutation.mutate({
      pdu_id: pduId,
      outlet_id: id,
      new_state: newState,
    });
  };

  const selectedOutlet = outlets.find((outlet: any) => 
    outlet.id === rowSelectionModel[0] || outlet.outlet_id === rowSelectionModel[0]
  );

  useEffect(() => {
    const items = [
      {
        label: 'Edit',
        icon: <EditIcon color="info" fontSize="small" />,
        onClick: () => {
          if (selectedOutlet) {
            setShowEditDialog(true);
          }
        },
        hasDivider: true,
      },
    ];
    setMenuItems([...items]);
  }, [selectedOutlet]);

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Name',
      width: 200,
      disableColumnMenu: true,
      renderCell: (params) => {
        return `Outlet ${params.row.id}`;
      },
    },
    {
      field: 'device_name',
      headerName: 'Description',
      width: 450,
      disableColumnMenu: true,
    },
    {
      field: 'state',
      headerName: 'Status',
      width: 100,
      disableColumnMenu: true,
      renderCell: (params: GridRenderCellParams) => (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: '100%',
            height: '100%',
          }}
        >
          {params.value !== undefined ? (
            <Indicator active={params.value === 'on'} />
          ) : (
            'N/A'
          )}
        </Box>
      ),
    },
    {
      field: 'split',
      headerName: 'Action (On/Off)',
      flex: 1,
      minWidth: 150,
      disableColumnMenu: true,
      sortable: true,
      valueGetter: (value, row) => row.state || 'off',
      renderCell: (params: GridRenderCellParams) => {
        const outletId = params.row.id ?? params.row.outlet_id ?? '';
        const currentState = params.row.state || 'off';
        const isToggling = togglingOutlets.has(Number(outletId));
        const isChecked = currentState === 'on';
        
        return (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'start',
              alignItems: 'center',
              width: '100%',
              height: '100%',
            }}
          >
            <IOSSwitch
              color="primary"
              checked={isChecked}
              onChange={() => handleToggleSplit(outletId, currentState)}
              disabled={isToggling}
              sx={{
                width: 50,
                height: 25,
                padding: 0,
                margin: 0,
                '& .MuiSwitch-switchBase': {
                  padding: 0.15,
                  '&.Mui-checked': {
                    transform: 'translateX(24px)',
                  },
                },
                '& .MuiSwitch-thumb': {
                  width: 20,
                  height: 20,
                },
                '& .MuiSwitch-track': {
                  borderRadius: 10,
                },
              }}
            />
          </Box>
        );
      },
    },
  ];

  const handleSelectionChange = (newSelection: GridRowSelectionModel) => {
    setRowSelectionModel(newSelection.slice(-1));
  };

  const handleEditSuccess = () => {
    if (onOutletUpdate) {
      onOutletUpdate();
    }
  };

  const handleRefresh = () => {
    if (onOutletUpdate) {
      onOutletUpdate();
      toast.success('Refreshed outlet data.');
    }
  };

  const prevIsRefetchingRef = useRef(isRefetching);
  useEffect(() => {
    if (prevIsRefetchingRef.current && !isRefetching && isRefreshingAfterToggle) {
      setIsRefreshingAfterToggle(false);
      setTogglingContent('');
    }
    prevIsRefetchingRef.current = isRefetching;
  }, [isRefetching, isRefreshingAfterToggle]);

  const selectedOutletId = selectedOutlet?.id || selectedOutlet?.outlet_id || '';
  const selectedOutletPortName = selectedOutlet ? `Outlet ${selectedOutlet.id || selectedOutlet.outlet_id}` : '';
  const selectedDeviceName = selectedOutlet?.device_name || '';
  const showLoader = isRefreshingAfterToggle;

  return (
    <>
      {showLoader && <PageLoading content={togglingContent} />} 
      <DataTableGrid
        rows={
          outlets.map((outlet: any) => ({
            ...outlet,
            id: outlet.id || outlet.outlet_id,
          })) || []
        }
        columns={columns}
        pagination
        rowSelectionModel={rowSelectionModel}
        handleSelectionChange={handleSelectionChange}
        itemsSelected={rowSelectionModel.length > 0}
        refreshData={handleRefresh}
        isRefetching={showLoader ? false : isRefetching}
        menuItems={menuItems}
      />
      {showEditDialog && selectedOutlet && (
        <DialogStandard
          open={showEditDialog}
          handleClose={() => setShowEditDialog(false)}
          title="Edit Outlet Device Name"
        >
          <PduOutletEdit
            handleClose={() => setShowEditDialog(false)}
            pduId={pduId}
            outletId={selectedOutletId}
            outletPortName={selectedOutletPortName}
            deviceName={selectedDeviceName}
            onSuccess={handleEditSuccess}
          />
        </DialogStandard>
      )}
    </>
  );
};
